import { useQuery } from '@tanstack/react-query';
import { api } from '@repo/api-sdk';
import { useTranslation } from 'react-i18next';
import { CalendarClock, MapPin, Clock, User } from 'lucide-react';
import { PremiumPageHeader, PageLoader } from '@repo/ui';

const DAY_ORDER = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const DAY_LABELS: Record<string, string> = {
  sunday: 'days.SUNDAY',
  monday: 'days.MONDAY',
  tuesday: 'days.TUESDAY',
  wednesday: 'days.WEDNESDAY',
  thursday: 'days.THURSDAY',
  friday: 'days.FRIDAY',
  saturday: 'days.SATURDAY',
};

const TODAY_INDEX = new Date().getDay();

function parseGroupName(name: string) {
  const parts = (name ?? '')
    .split(' | ')
    .map((p: string) => p.trim())
    .filter(Boolean);
  if (parts.length >= 2) {
    return { cycle: parts[0], track: parts[1] };
  }
  return { cycle: null, track: name };
}

const getNextDateForDay = (dayName: string) => {
  const targetDay = DAY_ORDER.indexOf(dayName.toLowerCase());
  if (targetDay === -1) return null;

  const now = new Date();
  const currentDay = now.getDay();
  let diff = targetDay - currentDay;
  if (diff < 0) diff += 7; // Always show current or next occurrence

  const result = new Date(now);
  result.setDate(now.getDate() + diff);
  return result;
};

const formatTimeTo12Hour = (time24: string, language: string) => {
  if (!time24) return '';
  const [h, m] = time24.split(':').map(Number);
  if (isNaN(h)) return time24;

  const date = new Date();
  date.setHours(h, m || 0, 0);
  return date.toLocaleTimeString(language === 'ar' ? 'ar-EG' : 'en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const SchedulePage = () => {
  const { t, i18n } = useTranslation();

  const { data, isLoading } = useQuery({
    queryKey: ['student-schedule'],
    queryFn: () => api.get('/student/schedule').then((res) => res.data?.data),
  });

  if (isLoading) {
    return <PageLoader message={t('common.loading')} className="min-h-64" />;
  }

  const weeklySchedule = data?.weeklySchedule ?? [];
  const upcomingSessions = data?.upcomingSessions ?? [];

  // Group by day
  const byDay: Record<string, any[]> = {};
  for (const item of weeklySchedule) {
    const dayKey = item.day.toLowerCase();
    if (!byDay[dayKey]) byDay[dayKey] = [];
    byDay[dayKey].push(item);
  }

  // Sort days in canonical order
  const sortedDays = DAY_ORDER.filter((d) => byDay[d]);

  return (
    <div className="space-y-6">
      <PremiumPageHeader
        title={t('student.schedule.title')}
        subtitle={t('student.schedule.subtitle', {
          defaultValue: 'Monitor your weekly sessions and upcoming appointments',
        })}
        icon={CalendarClock}
        iconClassName="text-primary"
        iconContainerClassName="bg-primary/10 border-primary/20"
      />

      {/* Weekly Recurring Schedule */}
      {sortedDays.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-black tracking-tight">{t('student.schedule.weekly')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {sortedDays.map((day) => {
              const isToday = DAY_ORDER.indexOf(day) === TODAY_INDEX;
              return (
                <div
                  key={day}
                  className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden transition-all hover:shadow-md"
                >
                  {/* Day header */}
                  <div
                    className={`flex items-center justify-between px-4 py-3 font-black text-sm tracking-wide ${
                      isToday ? 'bg-primary text-primary-foreground' : 'bg-primary/10 text-primary'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      {t(DAY_LABELS[day] || day)}
                      {isToday && (
                        <span className="text-[10px] font-black bg-white/20 px-2 py-0.5 rounded-full">
                          {t('common.today', { defaultValue: 'Today' })}
                        </span>
                      )}
                    </div>
                    {(() => {
                      const dDate = getNextDateForDay(day);
                      if (!dDate) return null;
                      return (
                        <span
                          dir="ltr"
                          className={`text-xs font-semibold ${isToday ? 'text-primary-foreground/90' : 'text-primary/70'}`}
                        >
                          {dDate.toLocaleDateString(i18n.language === 'ar' ? 'ar-EG' : 'en-US', {
                            day: 'numeric',
                            month: 'short',
                          })}
                        </span>
                      );
                    })()}
                  </div>

                  <div className="p-4 space-y-4">
                    {byDay[day].map((item: any, i: number) => {
                      const { cycle, track } = parseGroupName(item.group.name);
                      const branchName = item.branch
                        ? i18n.language === 'ar'
                          ? (item.branch.nameAr ?? item.branch.nameEn)
                          : (item.branch.nameEn ?? item.branch.nameAr)
                        : null;

                      return (
                        <div
                          key={i}
                          className="flex items-center justify-between gap-3 py-2 border-b border-border last:border-0"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              {cycle && (
                                <span className="shrink-0 px-2 py-0.5 rounded-md bg-primary text-primary-foreground text-[10px] font-black">
                                  {cycle}
                                </span>
                              )}
                              <p className="font-semibold text-sm truncate">{track}</p>
                            </div>
                            <p className="text-xs text-muted-foreground/60 font-mono mt-0.5">
                              {item.group.code}
                            </p>
                          </div>
                          <div className="text-end shrink-0 space-y-1">
                            <p className="text-sm font-bold flex items-center gap-1.5 justify-end text-foreground/80">
                              <Clock className="h-3 w-3 text-muted-foreground" />
                              <span dir="ltr" className="inline-block">
                                {formatTimeTo12Hour(item.startTime, i18n.language)} —{' '}
                                {formatTimeTo12Hour(item.endTime, i18n.language)}
                              </span>
                            </p>
                            {branchName && (
                              <p className="text-xs text-muted-foreground flex items-center gap-1 justify-end">
                                <MapPin className="h-3 w-3" />
                                {branchName}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Upcoming Sessions */}
      {upcomingSessions.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-black tracking-tight">{t('student.schedule.upcoming')}</h2>
          <div className="space-y-3">
            {upcomingSessions.map((session: any) => {
              // Match SessionsPage group formatting
              const groupFallback = session.groups?.length
                ? session.groups
                    .map((g: any) => {
                      const pts = (g.name || '').split('|').map((p: string) => p.trim());
                      return pts.length >= 2 ? `${pts[0]} | ${pts[1]}` : g.name;
                    })
                    .join(', ')
                : session.primaryGroup?.name;

              const groupDisplay = groupFallback || '—';

              const actualDate: Date | null = session.sessionDate
                ? new Date(session.sessionDate)
                : session.scheduledTime?.scheduledStartDate
                  ? new Date(session.scheduledTime.scheduledStartDate)
                  : session.startDate
                    ? new Date(session.startDate)
                    : null;

              const endRaw = session.scheduledTime?.scheduledEndDate || session.endDate;
              const timeLabelStr = actualDate
                ? actualDate.toLocaleTimeString(i18n.language === 'ar' ? 'ar-EG' : 'en-US', {
                    hour: '2-digit',
                    minute: '2-digit',
                  }) +
                  (endRaw
                    ? ` – ${new Date(endRaw).toLocaleTimeString(i18n.language === 'ar' ? 'ar-EG' : 'en-US', { hour: '2-digit', minute: '2-digit' })}`
                    : '')
                : null;

              return (
                <div
                  key={session._id}
                  className="rounded-2xl border border-border bg-card p-5 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <p className="font-bold truncate text-foreground text-sm">{groupDisplay}</p>

                      <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground flex-wrap font-medium">
                        {actualDate && (
                          <span className="flex items-center gap-1.5">
                            <CalendarClock className="h-4 w-4 text-primary/70 shrink-0" />
                            {actualDate.toLocaleDateString(
                              i18n.language === 'ar' ? 'ar-EG' : 'en-US',
                              { weekday: 'short', month: 'short', day: 'numeric' }
                            )}
                          </span>
                        )}

                        {timeLabelStr && (
                          <span className="flex items-center gap-1.5">
                            <Clock className="h-4 w-4 text-primary/70 shrink-0" />
                            <span dir="ltr" className="inline-block">
                              {timeLabelStr}
                            </span>
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      {session.host && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <User className="h-3.5 w-3.5" />
                          {session.host.name}
                        </div>
                      )}
                      {session.deliveryMode && (
                        <span className="text-xs px-3 py-1 rounded-full bg-primary/10 text-primary font-semibold border border-primary/10">
                          {session.deliveryMode}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!sortedDays.length && !upcomingSessions.length && (
        <div className="text-center py-16 text-muted-foreground">{t('student.schedule.empty')}</div>
      )}
    </div>
  );
};

export default SchedulePage;
